import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import {
  Avatar, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControlLabel, IconButton, InputAdornment, ListItemIcon, ListItemText, Menu, MenuItem, Select, Stack, Switch, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded'
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined'
import SearchIcon from '@mui/icons-material/Search'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import RepeatOutlinedIcon from '@mui/icons-material/RepeatOutlined'
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined'
import CloseIcon from '@mui/icons-material/Close'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined'
import { QrCodeDialog, qrPayload } from '../../components/QrCodeDialog'
import type { WorkOrder, WorkOrderPriority, WorkOrderType, WorkOrderKind, Site, Asset, AssetCategory, Vendor, ChecklistTemplateSummary, Team, DayOfWeek, ScheduleConfig, ScheduleConfigType } from '@cmms/types'
import type { Workflow } from '@cmms/api-client'
import { WorkflowPreview } from '../workflows/WorkflowPreview'

import { primary } from '../../theme/palette'
import { PageShell } from '../../layouts/PageShell'
import { api } from '../../lib/api'
import { tzWallToUtc, utcToTzWall, shortTzLabel } from '../../lib/tz-helpers'
import { useOrgSettings } from '../../hooks/useOrgSettings'
import { toast } from '../../store/snackbarStore'
import { UserAvatar } from '../../components/UserAvatar'
import { SearchableSelect } from '../../components/SearchableSelect'
import { LocationTreeSelect } from '../../components/LocationTreeSelect'
import { usePermissions } from '../../hooks/usePermissions'
import { useAuthStore } from '../../store/authStore'
import {
  adaptWorkOrders, PRIORITY_META,
  type DummyWorkOrder, type WoPriority,
} from './dummy'
import { KanbanView } from './views/KanbanView'
import { TableView } from './views/TableView'
import { CalendarView } from './views/CalendarView'
import { WorkloadView } from './views/WorkloadView'
import { WorkOrderDetailDrawer } from './WorkOrderDetailDrawer'
import { WorkModuleProvider } from './moduleContext'
import { StatRibbon } from './components/StatRibbon'
import { ViewSwitcher, type ViewMode } from './components/ViewSwitcher'
import { BulkActionBar } from '../../components/BulkActionBar'
import { useBulkSelection } from '../../hooks/useBulkSelection'
import { useConfirm } from '../../components/ConfirmDialog'
import { formatSiteName } from '../../lib/siteDisplayName'

type DueFilter = 'any' | 'overdue' | 'today' | 'week'
type TypeFilter = 'any' | 'reactive' | 'preventive' | 'inspection' | 'safety' | 'other'
const PRIORITIES: WoPriority[] = ['critical', 'high', 'medium', 'low']

const SelectCtx = createContext<(wo: DummyWorkOrder) => void>(() => {})
export const useSelectWorkOrder = () => useContext(SelectCtx)

// Row-actions context: right-clicking any card/row anywhere in the
// page (kanban card, table row, calendar agenda item, workload bar)
// calls this to pop the shared context menu (Duplicate / Show QR /
// Delete). The menu itself + the QR / confirm dialogs live at page
// level so every view uses the same handlers instead of reinventing
// them per-view.
const RowActionsCtx = createContext<(e: React.MouseEvent, wo: DummyWorkOrder) => void>(() => {})
export const useWorkOrderRowMenu = () => useContext(RowActionsCtx)

// Bulk-select context for the table view — null when bulk select isn't
// available (i.e. user doesn't have work_orders:delete). The table view
// reads this to decide whether to render the checkbox column.
type WoBulkApi = ReturnType<typeof useBulkSelection>
const WoBulkCtx = createContext<WoBulkApi | null>(null)
export const useWorkOrderBulk = () => useContext(WoBulkCtx)

/**
 * Renders the work-orders OR tasks module — same page, two URL families.
 *
 * Tasks live in the same DB table as work orders (`kind='task'`) but
 * are exposed as a separate API surface (`/api/v1/tasks/*`) gated by
 * the `tasks:*` permission family. The page derives every kind-specific
 * detail (perms, URLs, query keys, page title, "New" CTA shape, API
 * client namespace) from this one prop so the implementation stays
 * single-source.
 */
export function WorkOrdersPage({ moduleKind = 'work_order' }: { moduleKind?: WorkOrderKind } = {}) {
  const isTaskModule = moduleKind === 'task'
  const permPrefix  = isTaskModule ? 'tasks'      : 'work_orders'
  const basePath    = isTaskModule ? '/tasks'     : '/work-orders'
  const apiClient   = isTaskModule ? api.tasks    : api.workOrders
  const queryRoot   = isTaskModule ? 'tasks'      : 'work-orders'
  const titlePlural = isTaskModule ? 'Tasks'      : 'Work Orders'
  const titleSingular = isTaskModule ? 'task'     : 'work order'

  const qc = useQueryClient()
  const navigate = useNavigate()
  // The same component services both `/work-orders/:workOrderId` and
  // `/tasks/:taskId` — read whichever param the active route provides.
  const params = useParams<Record<string, string | undefined>>()
  const urlId = isTaskModule ? params.taskId : params.workOrderId
  const { can } = usePermissions()
  const me = useAuthStore((s) => s.user)
  // Avatar filter strip is only useful for users who actually see every
  // WO in the org. The backend's row-level visibility filter and this UI
  // gate are both keyed on the same permission so they stay in sync.
  // Super admins inherit it via permission wildcard; org_admin gets it
  // via the role-permission seed.
  const canSeeAllAssignees = me?.type === 'super_admin' || can(`${permPrefix}:read_all`)
  const [view, setView] = useState<ViewMode>('kanban')
  // Scheduled WOs are pre-active (their scheduled_for hasn't arrived,
  // or they're recurring templates). Hidden from the default list; the
  // user toggles them in via a chip that's only visible if they have
  // the `work_orders:view_scheduled` permission.
  const [showScheduled, setShowScheduled] = useState(false)
  // Honour either flavour of the perm so existing roles that hold the
  // older `work_orders:view_scheduled` keep the toggle on the Tasks
  // page even before the auto-mirror migration runs against their DB.
  const canSeeScheduled = can(`${permPrefix}:view_scheduled`)
    || can('work_orders:view_scheduled')
  const [search, setSearch] = useState('')
  const [due, setDue] = useState<DueFilter>('any')
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('any')
  // Task-only filter: 'scheduled' = has a future scheduled_for or a
  // schedule_config (recurring template), 'unscheduled' = ad-hoc work
  // with no date attached, 'any' = both. Hidden from the WO module
  // toolbar — WO maintenance work uses the existing typeFilter for
  // reactive/preventive/etc. instead.
  const [taskSchedFilter, setTaskSchedFilter] = useState<'any' | 'scheduled' | 'unscheduled'>('any')
  const [priority, setPriority] = useState<WoPriority | 'all'>('all')
  /** `null` = show all, `'unassigned'` = WOs with no user assignee, `<userId>` = filter to that user. */
  const [assigneeFilter, setAssigneeFilter] = useState<string | null>(null)
  // Site + location filter. Location filter is gated on a picked site
  // because locations live in a per-site tree (the locations endpoint
  // is `/sites/:siteId/locations`). Picking a new site resets the
  // location selection so we never carry a stale id across sites.
  const [siteFilter, setSiteFilter] = useState<string>('')
  const [locationFilter, setLocationFilter] = useState<string>('')
  const [searchParams, setSearchParams] = useSearchParams()
  const [selected, setSelected] = useState<DummyWorkOrder | null>(null)
  // `null` = no create dialog open. 'work_order' / 'task' selects the
  // create flavor. Same dialog backs both — it just hides the asset /
  // vendor / parts UI when kind='task' (soft-services tasks can't
  // carry hardware refs server-side either, so we don't let the user
  // try). See [CreateWorkOrderDialog] for the gating.
  const [createKind, setCreateKind] = useState<'work_order' | 'task' | null>(null)
  const createOpen = createKind !== null
  const setCreateOpen = (v: boolean) => setCreateKind(v ? (createKind ?? 'work_order') : null)

  // Duplicate-source WO/task. When set, the create dialog opens with
  // every editable field pre-populated from this record. Cleared on
  // dialog close (success or cancel) so a subsequent New-blank flow
  // starts from empty state.
  const [duplicateSource, setDuplicateSource] = useState<WorkOrder | null>(null)
  function handleDuplicate(source: WorkOrder) {
    setDuplicateSource(source)
    // Match the create-kind to the source so a duplicated task lands
    // as a task and a duplicated work order lands as a work order.
    setCreateKind(source.kind === 'task' ? 'task' : 'work_order')
  }

  // Edit-source WO/task — same dialog as Duplicate, but the submit
  // handler calls update() against this id instead of create(). Kept
  // as a separate flag from `duplicateSource` (rather than reusing it
  // with a boolean) so the two flows can never be confused about which
  // one is active.
  const [editSource, setEditSource] = useState<WorkOrder | null>(null)
  function handleEdit(source: WorkOrder) {
    setEditSource(source)
    setCreateKind(source.kind === 'task' ? 'task' : 'work_order')
  }

  // Auto-open create dialog from URL params (e.g. ?new=1&asset_id=xxx)
  const prefillAssetId = searchParams.get('asset_id') ?? ''
  const prefillLocationId = searchParams.get('location_id') ?? ''
  const prefillSiteId = searchParams.get('site_id') ?? ''
  const prefillAssignUser = searchParams.get('assign_user') ?? ''
  const prefillTitle = searchParams.get('request_title') ?? ''
  const prefillDesc = searchParams.get('request_desc') ?? ''
  const prefillRequestId = searchParams.get('request_id') ?? ''
  const prefillReporter = searchParams.get('assign_reporter') ?? ''
  useEffect(() => {
    if (searchParams.get('new') === '1' && can(`${permPrefix}:create`)) {
      // Inside the Tasks module the page only ever creates tasks,
      // regardless of any `?kind=` hint in the URL. On the Work
      // Orders module honour the hint, defaulting to the maintenance
      // flow if absent (preserves the legacy `?new=1` semantics).
      if (isTaskModule) {
        setCreateKind('task')
      } else {
        setCreateKind(searchParams.get('kind') === 'task' ? 'task' : 'work_order')
      }
      // Drop only the params this branch itself consumed (`new`,
      // `kind`) — NOT the whole query string. `prefillLocationId` /
      // `prefillSiteId` / `prefillAssetId` / etc. below are derived
      // fresh from `searchParams` on every render, not captured once
      // here; wiping the entire string (the old `setSearchParams({})`)
      // cleared them in the very same batched update that flips
      // `createOpen` to true, so the dialog that opens on the next
      // render read them back as empty strings. E.g. Locations'
      // "Use in new task" (`?new=1&location_id=..&site_id=..`) opened
      // the form with neither field populated.
      const next = new URLSearchParams(searchParams)
      next.delete('new')
      next.delete('kind')
      setSearchParams(next, { replace: true })
      return
    }
    // Deeplink from elsewhere in the app (e.g. the Overview map's
    // per-site "Open full list" arrow) — apply the site filter and
    // clear the param so it doesn't stick to subsequent navigations.
    const sid = searchParams.get('site_id')
    if (sid) {
      setSiteFilter(sid)
      setLocationFilter('')
      // Preserve any other params (assignee, etc.) — only drop the
      // ones we've already consumed.
      const next = new URLSearchParams(searchParams)
      next.delete('site_id')
      setSearchParams(next, { replace: true })
    }
  }, [])

  const workOrdersQ = useQuery({
    queryKey: [queryRoot, {
      q: search.trim() || undefined,
      priority: priority !== 'all' ? priority : undefined,
      showScheduled,
    }],
    queryFn: () => apiClient.list({
      // Scope by kind so the Work Orders module never lists tasks
      // (and vice-versa). Tasks routes also force this server-side;
      // setting it here makes the intent explicit and keeps the WO
      // list clean even though we hit /api/v1/work-orders.
      kind: moduleKind,
      q: search.trim() || undefined,
      priority: priority !== 'all' ? priority : undefined,
      // When the user opts in, fetch only scheduled WOs. The backend
      // hides them from the default list otherwise.
      ...(showScheduled ? { status: 'scheduled' } : {}),
    }),
  })

  // How many real (non-preview — status=scheduled skips the synthetic
  // preview step server-side, see work-orders.service.js) scheduled
  // rows exist for the current search/priority filters. The default
  // view above excludes them entirely, so StatRibbon's total would
  // otherwise silently be short by this count — same "X · +Y" split
  // used for the Kanban board's own Scheduled column. Skipped once
  // "Showing scheduled" is already on, since workOrdersQ already
  // includes them in that case.
  const scheduledCountQ = useQuery({
    queryKey: [queryRoot, 'scheduled-count', {
      q: search.trim() || undefined,
      priority: priority !== 'all' ? priority : undefined,
    }],
    queryFn: () => apiClient.list({
      kind: moduleKind,
      q: search.trim() || undefined,
      priority: priority !== 'all' ? priority : undefined,
      status: 'scheduled',
    }).then((rows) => rows.length),
    enabled: !showScheduled,
  })
  const hiddenScheduledCount = showScheduled ? 0 : (scheduledCountQ.data ?? 0)

  // Filter-bar lookups. Same query keys as the create dialog so the
  // react-query cache is shared and we don't double-fetch.
  const filterSitesQ = useQuery({ queryKey: ['sites'], queryFn: () => api.sites.list(), staleTime: 5 * 60_000 })
  const filterSites: Site[] = Array.isArray(filterSitesQ.data) ? filterSitesQ.data : []
  const filterLocationsQ = useQuery({
    queryKey: ['locations', siteFilter],
    queryFn: () => api.locations.list(siteFilter),
    enabled: !!siteFilter,
    staleTime: 60_000,
  })
  const filterLocations = Array.isArray(filterLocationsQ.data) ? filterLocationsQ.data : []
  const rawOrders: WorkOrder[] = Array.isArray(workOrdersQ.data) ? workOrdersQ.data : []
  const allAdapted = useMemo(() => adaptWorkOrders(rawOrders), [rawOrders])

  const filtered = useMemo(() => {
    let list = allAdapted
    const now = Date.now()
    const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0)
    const todayEnd = todayStart.getTime() + 86_400_000
    const weekEnd = now + 7 * 86_400_000

    if (due !== 'any') {
      list = list.filter((w) => {
        const d = w.dueDate.getTime()
        // Compare against the start of today, not the exact current
        // moment — due_date has no time component, so `< now` counted
        // anything due today as overdue for the rest of the day.
        if (due === 'overdue') return d < todayStart.getTime() && w.status !== 'closed' && w.status !== 'resolved'
        if (due === 'today') return d >= todayStart.getTime() && d < todayEnd
        if (due === 'week') return d >= now && d <= weekEnd
        return true
      })
    }
    if (typeFilter !== 'any') {
      list = list.filter((w) => (w._raw.work_type ?? 'reactive') === typeFilter)
    }
    if (isTaskModule && taskSchedFilter !== 'any') {
      list = list.filter((w) => {
        const isSched = !!w._raw.scheduled_for || !!w._raw.schedule_config
        return taskSchedFilter === 'scheduled' ? isSched : !isSched
      })
    }
    if (assigneeFilter) {
      list = list.filter((w) => assigneeFilter === 'unassigned'
        ? !w._raw.assigned_to_user_id
        : w._raw.assigned_to_user_id === assigneeFilter)
    }
    if (siteFilter) {
      list = list.filter((w) => w._raw.site_id === siteFilter)
    }
    if (locationFilter) {
      list = list.filter((w) => w._raw.location_id === locationFilter)
    }
    return list
  }, [allAdapted, due, typeFilter, taskSchedFilter, isTaskModule, assigneeFilter, siteFilter, locationFilter])

  // The set of distinct user-assignees actually present in the loaded WOs —
  // ordered by frequency so the busiest people land first. Cap at a sane
  // number so the strip stays scannable; overflow goes into a "+N" chip.
  const assigneeOptions = useMemo(() => {
    if (!canSeeAllAssignees) return []
    const counts = new Map<string, { id: string; name: string; avatarUrl: string | null; count: number }>()
    for (const w of allAdapted) {
      const id = w._raw.assigned_to_user_id
      if (!id || !w._raw.assigned_to_user_name) continue
      const existing = counts.get(id)
      if (existing) existing.count += 1
      else counts.set(id, {
        id,
        name: w._raw.assigned_to_user_name,
        avatarUrl: w._raw.assigned_to_user_avatar ?? null,
        count: 1,
      })
    }
    return Array.from(counts.values()).sort((a, b) => b.count - a.count)
  }, [allAdapted, canSeeAllAssignees])

  // Sync URL → selected state. Clears `selected` outright when the id
  // doesn't match anything in this module's (kind-scoped) list — e.g.
  // a stale/cross-kind link (a task id opened under /work-orders/:id)
  // — rather than leaving whatever was previously selected on screen.
  // The old `if (match) setSelected(match)` silently no-opped on a
  // miss, so a bad link just kept showing an unrelated WO instead of
  // the intended one.
  useEffect(() => {
    if (urlId && allAdapted.length > 0) {
      const match = allAdapted.find((w) => w.id === urlId)
      setSelected(match ?? null)
    }
  }, [urlId, allAdapted])

  function handleSelect(wo: DummyWorkOrder) {
    setSelected(wo)
    navigate(`${basePath}/${wo.id}`, { replace: true })
  }

  function handleClose() {
    setSelected(null)
    navigate(basePath, { replace: true })
    qc.invalidateQueries({ queryKey: [queryRoot] })
  }

  const confirmDlg = useConfirm()
  // Org timezone — used when building the duplicate prefill so a
  // one-shot `scheduled_for` UTC moment round-trips into the create
  // dialog's local datetime-local input as the correct wall-clock time.
  const { prefs: pageOrgPrefs } = useOrgSettings()
  const pageOrgTz = pageOrgPrefs.timezone || 'UTC'
  const bulk = useBulkSelection()
  const bulkDeleteM = useMutation({
    mutationFn: (ids: string[]) => apiClient.bulkRemove(ids),
    onSuccess: (res) => {
      const okCount = res.deleted.length
      const skipCount = res.skipped.length
      if (okCount > 0 && skipCount === 0) toast.success(`Deleted ${okCount} ${titleSingular}${okCount === 1 ? '' : 's'}`)
      else if (okCount > 0 && skipCount > 0) toast.success(`Deleted ${okCount}, skipped ${skipCount}`)
      else toast.error('Nothing was deleted')
      qc.invalidateQueries({ queryKey: [queryRoot] })
      bulk.clear()
      if (selected && res.deleted.includes(selected.id)) setSelected(null)
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Bulk delete failed'),
  })
  async function handleBulkDelete() {
    const ids = bulk.ids
    if (ids.length === 0) return
    const ok = await confirmDlg({
      title: `Delete ${ids.length} ${titleSingular}${ids.length === 1 ? '' : 's'}?`,
      message: 'They will be soft-deleted and hidden from this list. You can re-import them anytime.',
      confirmLabel: 'Delete',
      variant: 'danger',
    })
    if (ok) bulkDeleteM.mutate(ids)
  }

  const isFiltering = Boolean(search) || due !== 'any' || priority !== 'all' || typeFilter !== 'any' || assigneeFilter !== null || siteFilter !== '' || locationFilter !== '' || (isTaskModule && taskSchedFilter !== 'any')

  // ── Row-level context menu (right-click) ────────────────────────
  // Fires on any card/row and offers Duplicate / Show QR / Delete
  // without forcing the user to open the detail drawer. Position is
  // anchored to the cursor via `anchorReference: 'anchorPosition'`.
  const [rowMenu, setRowMenu] = useState<{ x: number; y: number; wo: DummyWorkOrder } | null>(null)
  const [qrTarget, setQrTarget] = useState<DummyWorkOrder | null>(null)
  function openRowMenu(e: React.MouseEvent, wo: DummyWorkOrder) {
    e.preventDefault()
    e.stopPropagation()
    setRowMenu({ x: e.clientX, y: e.clientY, wo })
  }
  async function handleRowDelete(target: DummyWorkOrder) {
    const ok = await confirmDlg({
      title: `Delete ${titleSingular}?`,
      message: `"${target.title}" will be soft-deleted and hidden from this list.`,
      confirmLabel: 'Delete',
      variant: 'danger',
    })
    if (ok) bulkDeleteM.mutate([target.id])
  }

  const toolbar = (
    <Box>
      <StatRibbon workOrders={filtered} hiddenScheduledCount={hiddenScheduledCount} />
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'stretch', md: 'center' }} spacing={1.5}>
        <ViewSwitcher value={view} onChange={setView} />
        {canSeeAllAssignees && assigneeOptions.length > 0 && (
          <Box sx={{ minWidth: 0, flex: { xs: '0 0 auto', md: '1 1 auto' }, maxWidth: { md: 520 } }}>
            <AssigneeFilterStrip
              options={assigneeOptions}
              selected={assigneeFilter}
              onSelect={setAssigneeFilter}
            />
          </Box>
        )}
        <Box sx={{ flex: 1 }} />
        {isFiltering && (
          <Chip label={`${filtered.length} / ${allAdapted.length}`} size="small"
            sx={{ height: 24, fontSize: 11.5, fontWeight: 600, bgcolor: 'action.hover', color: 'text.secondary' }} />
        )}
        <TextField placeholder="Search" value={search} onChange={(e) => setSearch(e.target.value)} size="small"
          sx={{ width: { xs: '100%', md: 220 }, '& .MuiOutlinedInput-root': { bgcolor: 'background.paper', fontSize: 13, '& fieldset': { borderColor: 'divider' } } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 15, color: 'text.secondary' }} /></InputAdornment> }} />
        <MinimalSelect placeholder="Due" value={due} onChange={(v) => setDue(v as DueFilter)} width={120}
          options={[{ value: 'any', label: 'Any time' }, { value: 'overdue', label: 'Overdue' }, { value: 'today', label: 'Today' }, { value: 'week', label: 'This week' }]} />
        {isTaskModule ? (
          <MinimalSelect placeholder="Task type" value={taskSchedFilter}
            onChange={(v) => setTaskSchedFilter(v as 'any' | 'scheduled' | 'unscheduled')}
            width={150}
            options={[
              { value: 'any',         label: 'Any task'    },
              { value: 'scheduled',   label: 'Scheduled'   },
              { value: 'unscheduled', label: 'Unscheduled' },
            ]} />
        ) : (
          <MinimalSelect placeholder="Type" value={typeFilter} onChange={(v) => setTypeFilter(v as TypeFilter)} width={140}
            options={[
              { value: 'any',        label: 'Any type'    },
              { value: 'reactive',   label: 'Reactive'    },
              { value: 'preventive', label: 'Preventive'  },
              { value: 'inspection', label: 'Inspection'  },
              { value: 'safety',     label: 'Safety'      },
              { value: 'other',      label: 'Other'       },
            ]} />
        )}
        <MinimalSelect placeholder="Priority" value={priority} onChange={(v) => setPriority(v as WoPriority | 'all')} width={120}
          options={[{ value: 'all', label: 'Any' }, ...PRIORITIES.map((p) => ({ value: p, label: PRIORITY_META[p]!.label }))]} />
        {/* Site + Location filters — searchable because long site/location
            lists are hard to scan in a plain Select. Picking a new site
            resets the location filter (locations are site-scoped). */}
        <Box sx={{ width: 160 }}>
          <SearchableSelect
            value={siteFilter}
            onChange={(v) => { setSiteFilter(v); setLocationFilter('') }}
            options={filterSites}
            getValue={(s) => s.id}
            getLabel={formatSiteName}
            placeholder="Site"
          />
        </Box>
        <Box sx={{ width: 180 }}>
          <SearchableSelect
            value={locationFilter}
            onChange={setLocationFilter}
            options={filterLocations}
            getValue={(l) => l.id}
            getLabel={(l) => l.name}
            placeholder={siteFilter ? 'Location' : 'Pick site first'}
            disabled={!siteFilter}
            loading={!!siteFilter && filterLocationsQ.isLoading}
          />
        </Box>
        {canSeeScheduled && (
          <Chip
            label={showScheduled ? 'Showing scheduled' : 'Active only'}
            size="small"
            color={showScheduled ? 'primary' : 'default'}
            variant={showScheduled ? 'filled' : 'outlined'}
            onClick={() => setShowScheduled((v) => !v)}
            sx={{ fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
          />
        )}
      </Stack>
    </Box>
  )

  return (
    <WorkModuleProvider isTask={isTaskModule}>
    <SelectCtx.Provider value={handleSelect}>
    <RowActionsCtx.Provider value={openRowMenu}>
    <WoBulkCtx.Provider value={can(`${permPrefix}:delete`) ? bulk : null}>
      <PageShell title={titlePlural} toolbar={toolbar} scrollContent={false}
        actions={
          can(`${permPrefix}:create`) ? (
            // Each module creates only its own kind now that Tasks lives
            // as a separate top-level module. The kind-picker dropdown
            // is gone — the WO page renders "New work order", the Tasks
            // page renders "New task". Same dialog backs both; `kind`
            // controls the asset / vendor / parts visibility inside it.
            <NewCtaMenu
              onPick={setCreateKind}
              restrictTo={isTaskModule ? 'task' : 'work_order'}
            />
          ) : undefined
        }>
        <Box sx={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', pb: { xs: 3, md: 4 } }}>
          <BulkActionBar count={bulk.count} label={titleSingular}
            deleting={bulkDeleteM.isPending}
            onClear={bulk.clear} onDelete={handleBulkDelete} />
          {workOrdersQ.isLoading ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={28} /></Box>
          ) : (
            <AnimatePresence mode="wait" initial={false}>
              <motion.div key={view}
                initial={{ opacity: 0, y: 10, scale: 0.992 }} animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -6, scale: 0.996 }} transition={{ duration: 0.32, ease: [0.32, 0.72, 0, 1] }}
                style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                {view === 'kanban'   && <KanbanView   workOrders={filtered} hiddenScheduledCount={hiddenScheduledCount} />}
                {view === 'table'    && <TableView    workOrders={filtered} />}
                {view === 'calendar' && <CalendarView workOrders={filtered} />}
                {view === 'workload' && <WorkloadView workOrders={filtered} />}
              </motion.div>
            </AnimatePresence>
          )}
        </Box>
      </PageShell>

      <WorkOrderDetailDrawer wo={selected} onClose={handleClose} onDuplicate={handleDuplicate} onEdit={handleEdit} />
      {createOpen && createKind && <CreateWorkOrderDialog
        // Remount whenever the source changes so `useState` initializers
        // re-run with the new prefill — otherwise opening Duplicate/Edit
        // on a second WO in the same session would keep the first
        // one's pre-populated values.
        key={editSource?.id ?? duplicateSource?.id ?? 'blank'}
        kind={createKind}
        mode={editSource ? 'edit' : 'create'}
        workOrderId={editSource?.id}
        onClose={() => { setCreateKind(null); setDuplicateSource(null); setEditSource(null) }}
        onCreated={() => { qc.invalidateQueries({ queryKey: [queryRoot] }); qc.invalidateQueries({ queryKey: ['requests'] }); setCreateKind(null); setDuplicateSource(null); setEditSource(null) }}
        prefill={editSource
          ? buildDuplicatePrefill(editSource, pageOrgTz, 'exact')
          : duplicateSource
            ? buildDuplicatePrefill(duplicateSource, pageOrgTz)
            : { asset_id: prefillAssetId, location_id: prefillLocationId, site_id: prefillSiteId, assign_user: prefillAssignUser, title: prefillTitle, description: prefillDesc, request_id: prefillRequestId, reporter_id: prefillReporter }} />}

      {/* Right-click row menu — Duplicate / Show QR / Delete. Same
          three actions as the drawer overflow menu, but reachable
          from any card/row without opening the drawer first. */}
      <Menu
        open={Boolean(rowMenu)}
        onClose={() => setRowMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={rowMenu ? { top: rowMenu.y, left: rowMenu.x } : undefined}
        slotProps={{ paper: { sx: { minWidth: 200, borderRadius: 1.5 } } }}
      >
        {rowMenu && can(`${permPrefix}:update`) && (
          <MenuItem onClick={() => {
            const source = rowMenu.wo._raw
            setRowMenu(null)
            handleEdit(source)
          }} sx={{ fontSize: 13 }}>
            <ListItemIcon><EditOutlinedIcon sx={{ fontSize: 18 }} /></ListItemIcon>
            <ListItemText>Edit {titleSingular}</ListItemText>
          </MenuItem>
        )}
        {rowMenu && can(`${permPrefix}:create`) && (
          <MenuItem onClick={() => {
            const source = rowMenu.wo._raw
            setRowMenu(null)
            handleDuplicate(source)
          }} sx={{ fontSize: 13 }}>
            <ListItemIcon><ContentCopyOutlinedIcon sx={{ fontSize: 18 }} /></ListItemIcon>
            <ListItemText>Duplicate {titleSingular}</ListItemText>
          </MenuItem>
        )}
        {rowMenu && (
          <MenuItem onClick={() => {
            const target = rowMenu.wo
            setRowMenu(null)
            setQrTarget(target)
          }} sx={{ fontSize: 13 }}>
            <ListItemIcon><QrCode2OutlinedIcon sx={{ fontSize: 18 }} /></ListItemIcon>
            <ListItemText>Show QR code</ListItemText>
          </MenuItem>
        )}
        {rowMenu && can(`${permPrefix}:delete`) && rowMenu.wo.status !== 'resolved' && (
          <MenuItem onClick={() => {
            const target = rowMenu.wo
            setRowMenu(null)
            handleRowDelete(target)
          }} sx={{ fontSize: 13, color: 'error.main' }}>
            <ListItemIcon><DeleteOutlineIcon sx={{ fontSize: 18, color: 'error.main' }} /></ListItemIcon>
            <ListItemText>Delete {titleSingular}</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* QR dialog for the right-click "Show QR code" action. Kept
          at page level so a right-click doesn't have to detour
          through the detail drawer to reach the same dialog. */}
      {qrTarget && (
        <QrCodeDialog open onClose={() => setQrTarget(null)}
          payload={qrPayload.workOrder(qrTarget.id)}
          title={qrTarget.title}
          subtitle={qrTarget._raw.wo_code ?? undefined}
          kind={titleSingular === 'task' ? 'Task' : 'Work order'} />
      )}
    </WoBulkCtx.Provider>
    </RowActionsCtx.Provider>
    </SelectCtx.Provider>
    </WorkModuleProvider>
  )
}

// ─── "New" CTA dropdown ─────────────────────────────────────────────────────
//
// Single primary button with a built-in chevron that opens a small menu
// of options — Task (soft services, blue) or Work Order (maintenance,
// teal). The customer asked for one button instead of two side-by-side
// so the toolbar stays tidy; the menu still makes the choice explicit
// before the heavy create dialog opens.
function NewCtaMenu({ onPick, restrictTo }: {
  onPick: (kind: 'work_order' | 'task') => void
  /** When set, skip the dropdown and render a plain primary button
   *  that picks this kind directly. Used by the Tasks module so the
   *  button reads "New task" with no submenu — there's no ambiguity
   *  when the page only creates one kind. */
  restrictTo?: 'work_order' | 'task'
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  if (restrictTo) {
    return (
      <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
        transition={{ type: 'spring', stiffness: 400, damping: 28 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          onClick={() => onPick(restrictTo)}
          sx={{ fontSize: 13, fontWeight: 600, boxShadow: primary.glow }}>
          {restrictTo === 'task' ? 'New task' : 'New work order'}
        </Button>
      </motion.div>
    )
  }
  return (
    <>
      <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
        transition={{ type: 'spring', stiffness: 400, damping: 28 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          endIcon={<KeyboardArrowDownRoundedIcon sx={{ fontSize: 18 }} />}
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{ fontSize: 13, fontWeight: 600, boxShadow: primary.glow }}>
          New
        </Button>
      </motion.div>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { borderRadius: 2, minWidth: 280, mt: 0.75, boxShadow: '0 12px 28px -8px rgba(0,0,0,0.18)' } } }}
      >
        <MenuItem
          onClick={() => { onPick('task'); setAnchor(null) }}
          sx={{ py: 1.25, gap: 1.25 }}
        >
          <ListItemIcon sx={{ minWidth: 0 }}>
            <Box sx={{
              width: 32, height: 32, borderRadius: 1.5,
              bgcolor: 'rgba(29, 78, 216, 0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <TaskAltRoundedIcon sx={{ fontSize: 18, color: '#1D4ED8' }} />
            </Box>
          </ListItemIcon>
          <ListItemText
            primary="Task"
            primaryTypographyProps={{ sx: { fontSize: 14, fontWeight: 700, letterSpacing: '-0.01em' } }}
            secondary="Soft-services — no asset, vendor or parts"
            secondaryTypographyProps={{ sx: { fontSize: 11.5, mt: 0.25 } }}
          />
        </MenuItem>
        <MenuItem
          onClick={() => { onPick('work_order'); setAnchor(null) }}
          sx={{ py: 1.25, gap: 1.25 }}
        >
          <ListItemIcon sx={{ minWidth: 0 }}>
            <Box sx={{
              width: 32, height: 32, borderRadius: 1.5,
              bgcolor: 'rgba(15, 118, 110, 0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <BuildCircleOutlinedIcon sx={{ fontSize: 18, color: '#0F766E' }} />
            </Box>
          </ListItemIcon>
          <ListItemText
            primary="Work Order"
            primaryTypographyProps={{ sx: { fontSize: 14, fontWeight: 700, letterSpacing: '-0.01em' } }}
            secondary="Maintenance — asset, vendor, parts available"
            secondaryTypographyProps={{ sx: { fontSize: 11.5, mt: 0.25 } }}
          />
        </MenuItem>
      </Menu>
    </>
  )
}

// ─── Create dialog ──────────────────────────────────────────────────────────

const PRIORITY_COLORS: Record<string, string> = { low: '#059669', medium: '#D97706', high: '#EA580C', critical: '#DC2626' }
const DAY_LABELS: { key: DayOfWeek; short: string }[] = [
  { key: 'sun', short: 'S' }, { key: 'mon', short: 'M' }, { key: 'tue', short: 'T' },
  { key: 'wed', short: 'W' }, { key: 'thu', short: 'T' }, { key: 'fri', short: 'F' }, { key: 'sat', short: 'S' },
]
const DAY_FULL: Record<string, string> = { sun: 'Sunday', mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday', fri: 'Friday', sat: 'Saturday' }

// ─── Assignee filter strip ───────────────────────────────────────────────────
//
// A row of avatar bubbles (Slack-style) — click one to filter the WO list to
// that person's tickets. Leftmost bubble is "Unassigned"; gating is up at the
// call site so this never renders for users who can't see all tickets.

interface AssigneeOption {
  id: string
  name: string
  avatarUrl: string | null
  count: number
}

function AssigneeFilterStrip({
  options, selected, onSelect,
}: {
  options: AssigneeOption[]
  selected: string | null
  onSelect: (id: string | null) => void
}) {
  const MAX_VISIBLE = 8
  const visible = options.slice(0, MAX_VISIBLE)
  const overflow = options.slice(MAX_VISIBLE)

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.75}
      sx={{
        py: 0.5, px: 0.25,
        overflowX: 'auto', overflowY: 'visible',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      {/* Unassigned bubble — first slot, always there */}
      <FilterBubble
        label="Unassigned"
        active={selected === 'unassigned'}
        onClick={() => onSelect(selected === 'unassigned' ? null : 'unassigned')}
      >
        <PersonOutlinedIcon sx={{ fontSize: 18 }} />
      </FilterBubble>

      {/* User bubbles */}
      {visible.map((opt) => {
        const active = selected === opt.id
        return (
          <Tooltip key={opt.id} title={`${opt.name} · ${opt.count}`} arrow>
            <Box
              role="button"
              onClick={() => onSelect(active ? null : opt.id)}
              sx={{
                cursor: 'pointer',
                borderRadius: 99,
                p: '2px',
                border: '2px solid',
                borderColor: active ? 'primary.main' : 'transparent',
                transition: 'border-color 120ms, transform 120ms',
                '&:hover': { transform: 'scale(1.05)' },
                flexShrink: 0,
              }}
            >
              <UserAvatar name={opt.name} avatarUrl={opt.avatarUrl} size={36}
                sx={{ fontSize: 13, fontWeight: 700 }} />
            </Box>
          </Tooltip>
        )
      })}

      {overflow.length > 0 && (
        <Tooltip title={overflow.map((o) => o.name).join(', ')} arrow>
          <Box sx={{
            width: 36, height: 36, borderRadius: 99, flexShrink: 0,
            bgcolor: 'action.hover', color: 'text.secondary',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 700,
          }}>
            +{overflow.length}
          </Box>
        </Tooltip>
      )}

      {/* Clear filter pill at the end — appears only when something's selected */}
      {selected && (
        <Box
          role="button"
          onClick={() => onSelect(null)}
          sx={{
            ml: 0.5, px: 1.25, height: 28, borderRadius: 99,
            display: 'inline-flex', alignItems: 'center', gap: 0.5,
            cursor: 'pointer',
            bgcolor: 'action.hover',
            fontSize: 11.5, fontWeight: 700, color: 'text.secondary',
            flexShrink: 0,
            '&:hover': { color: 'text.primary' },
          }}
        >
          <CloseIcon sx={{ fontSize: 13 }} />
          Clear
        </Box>
      )}
    </Stack>
  )
}

function FilterBubble({
  active, onClick, children, label,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
  label: string
}) {
  return (
    <Tooltip title={label} arrow>
      <Box
        role="button"
        onClick={onClick}
        sx={{
          width: 40, height: 40, borderRadius: 99, p: '2px',
          border: '2px solid',
          borderColor: active ? 'primary.main' : 'transparent',
          flexShrink: 0,
          cursor: 'pointer',
          transition: 'border-color 120ms, transform 120ms',
          '&:hover': { transform: 'scale(1.05)' },
        }}
      >
        <Box sx={{
          width: '100%', height: '100%', borderRadius: 99,
          bgcolor: 'action.hover', color: 'text.secondary',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {children}
        </Box>
      </Box>
    </Tooltip>
  )
}

/// Full prefill shape — used by both the URL-param flow (Requests →
/// Create WO deeplink) and the Duplicate flow (copy every field of an
/// existing WO/task). Every field is optional so callers only have to
/// populate what they know.
export interface CreateWorkOrderPrefill {
  // Basic identity + text
  title?: string
  description?: string
  priority?: WorkOrderPriority
  work_type?: WorkOrderType
  category?: string
  report_template_id?: string
  // Location + asset
  site_id?: string
  location_id?: string
  asset_id?: string
  // People
  assign_user?: string   // legacy singular — surfaces as first assignee
  assignee_ids?: string[] // multi-user (Duplicate flow, future URL params)
  team_id?: string
  reporter_id?: string
  // Scheduling / effort
  due_date?: string      // YYYY-MM-DD
  start_date?: string    // YYYY-MM-DD
  estimated_minutes?: number
  // Checklist(s)
  checklist_id?: string
  // Vendor + signature gates
  vendor_id?: string
  require_close_signature?: boolean
  require_acknowledge_signature?: boolean
  // Calendar scheduling. Populated by the Duplicate flow so a scheduled
  // task/WO round-trips its schedule into the new dialog with the same
  // "Task type" pre-selected (ad-hoc / one-shot / recurring) and the
  // recurrence config or one-shot datetime pre-filled.
  schedule_mode?: 'off' | 'one_shot' | 'recurring'
  scheduled_for_wall?: string    // datetime-local string in org tz
  schedule_config?: ScheduleConfig | null
  preview_ahead_count?: number | null
  preview_ahead_unit?: 'days' | 'weeks' | 'months' | 'years' | null
  // Deep-link back-refs (not user-editable)
  request_id?: string
}

/// Snapshot a WO/task into the create-dialog prefill shape. Copies
/// every editable field so the user sees a fully-populated form and
/// can adjust before hitting Create — nothing is created until the
/// user submits the form themselves.
///
/// Includes the Task type / schedule section (mode + one-shot moment
/// or recurrence config) so duplicating a scheduled or recurring
/// row lands with the schedule pre-populated — otherwise the copy
/// looked ad-hoc even when the source was recurring.
///
/// Deliberately does NOT copy:
///   • approvers / workflow_id — chain state is per-instance.
///   • lifecycle fields (status, resolved_at, sla_responded_at, etc.)
///     — the new record starts fresh.
export function buildDuplicatePrefill(
  source: WorkOrder, orgTz: string,
  // 'copy' (default) prefixes the title for the Duplicate flow so the
  // new row reads as a copy at a glance. 'exact' is used by Edit —
  // same snapshot, but the title should round-trip unchanged since
  // we're re-saving the SAME record, not creating a new one.
  titleMode: 'copy' | 'exact' = 'copy',
): CreateWorkOrderPrefill {
  const dueIso = source.due_date ? source.due_date.slice(0, 10) : undefined
  const startIso = source.start_date ? source.start_date.slice(0, 10) : undefined

  // Task type / schedule mode is inferred from the source row:
  //   is_schedule_template → 'recurring' (calendar-driven template)
  //   scheduled_for set    → 'one_shot' (single future firing)
  //   neither              → 'off' (ad-hoc)
  let scheduleMode: 'off' | 'one_shot' | 'recurring' = 'off'
  if (source.is_schedule_template) scheduleMode = 'recurring'
  else if (source.scheduled_for)   scheduleMode = 'one_shot'

  // Convert the one-shot moment back to a wall-clock string in the
  // org's timezone so the datetime-local input picks up the same
  // point in time the user originally chose.
  const scheduledForWall = source.scheduled_for
    ? utcToTzWall(source.scheduled_for, orgTz)
    : undefined

  return {
    title:       titleMode === 'exact' ? source.title : `Copy of ${source.title}`,
    description: source.description ?? undefined,
    priority:    source.priority,
    work_type:   source.work_type,
    category:    source.category ?? undefined,
    report_template_id: source.report_template_id ?? undefined,
    site_id:     source.site_id,
    location_id: source.location_id ?? undefined,
    asset_id:    source.asset_id ?? undefined,
    // Multi-user duplicate: carry every assignee across. Falls back
    // to the legacy single pointer when the source predates the
    // migration.
    assignee_ids: source.assignees && source.assignees.length > 0
      ? source.assignees.map((a) => a.id)
      : (source.assigned_to_user_id ? [source.assigned_to_user_id] : undefined),
    assign_user: source.assigned_to_user_id ?? undefined,
    team_id:     source.assigned_to_team_id ?? undefined,
    reporter_id: source.reporter_id ?? undefined,
    due_date:    dueIso,
    start_date:  startIso,
    estimated_minutes: source.estimated_minutes ?? undefined,
    checklist_id: source.checklist_id ?? undefined,
    vendor_id:    source.vendor_id ?? undefined,
    require_close_signature:       source.require_close_signature,
    require_acknowledge_signature: source.require_acknowledge_signature,
    schedule_mode:       scheduleMode,
    scheduled_for_wall:  scheduledForWall,
    schedule_config:     source.schedule_config ?? undefined,
    preview_ahead_count: source.preview_ahead_count ?? undefined,
    preview_ahead_unit:  source.preview_ahead_unit ?? undefined,
  }
}

function CreateWorkOrderDialog({ kind = 'work_order', mode = 'create', workOrderId, onClose, onCreated, prefill }: {
  kind?: 'work_order' | 'task'
  /** 'edit' reuses this exact same form, pre-filled from the existing
   *  record (see [buildDuplicatePrefill]'s 'exact' title mode) — the
   *  submit handler below branches to call update()/assign() against
   *  `workOrderId` instead of create(). */
  mode?: 'create' | 'edit'
  workOrderId?: string
  onClose: () => void
  onCreated: () => void
  prefill?: CreateWorkOrderPrefill
}) {
  const isEdit = mode === 'edit'
  // Local QueryClient — used to invalidate the `requests` query after
  // the optional auto-link so the Requests UI swaps its action buttons
  // from "Create work order" to "View linked work order" immediately.
  const qc = useQueryClient()
  const isTask = kind === 'task'
  // Route the create / assign / approval-step / checklist-link calls
  // to the namespace that matches the picked kind. Tasks land under
  // `/api/v1/tasks` so they're gated by the `tasks:*` permission family
  // even when the dialog was opened from the Work Orders page.
  const apiNs = isTask ? api.tasks : api.workOrders
  // Org timezone — everything the admin types in this form is treated as
  // wall-clock time in this zone. Browser-local interpretation would
  // drift hours if the admin's laptop is in a different tz from the
  // org. See lib/tz-helpers for the conversion math.
  const { prefs: orgPrefs } = useOrgSettings()
  const orgTz = orgPrefs.timezone || 'UTC'
  const tzLabel = `in ${shortTzLabel(orgTz)}`

  const [title, setTitle] = useState(prefill?.title ?? '')
  const [description, setDescription] = useState(prefill?.description ?? '')
  const [priority, setPriority] = useState<WorkOrderPriority>(prefill?.priority ?? 'medium')
  const [siteId, setSiteId] = useState(prefill?.site_id ?? '')
  const [locationId, setLocationId] = useState(prefill?.location_id ?? '')
  const [assetId, setAssetId] = useState(prefill?.asset_id ?? '')
  const [dueDate, setDueDate] = useState(prefill?.due_date ?? '')
  const [startDate, setStartDate] = useState(prefill?.start_date ?? '')
  // Estimated minutes is a single INT on the WO row; the form splits
  // it into hours + mins for the two side-by-side pickers. Duplicate
  // flow passes the total minutes so we recompute the split here.
  const [estHours, setEstHours] = useState(
    prefill?.estimated_minutes ? String(Math.floor(prefill.estimated_minutes / 60)) : ''
  )
  const [estMins, setEstMins] = useState(
    prefill?.estimated_minutes ? String(prefill.estimated_minutes % 60) : ''
  )
  const [workType, setWorkType] = useState<WorkOrderType>(prefill?.work_type ?? 'reactive')
  const [checklistIds, setChecklistIds] = useState<string[]>(
    prefill?.checklist_id ? [prefill.checklist_id] : []
  )
  // Calendar scheduling (separate from closure-driven `recurrence`):
  // mode controls the UI; 'off' = WO is active immediately; 'one_shot' =
  // single future datetime; 'recurring' = template that spawns children.
  // Schedule fields also honour the prefill so Duplicate carries the
  // Task-type section over verbatim. `schedule_config` shape has both
  // new (times / days_of_month / dates) and legacy (time / day_of_month
  // / month) forms — we pull from the new fields first and fall back
  // to the legacy singles so old rows still round-trip.
  const pfConfig = prefill?.schedule_config ?? null
  const [scheduleMode, setScheduleMode] = useState<'off' | 'one_shot' | 'recurring'>(prefill?.schedule_mode ?? 'off')
  const [scheduledFor, setScheduledFor] = useState(prefill?.scheduled_for_wall ?? '')
  const [scheduleType, setScheduleType] = useState<ScheduleConfigType>(pfConfig?.type ?? 'daily')
  const [scheduleTimes, setScheduleTimes] = useState<string[]>(
    pfConfig?.times && pfConfig.times.length > 0
      ? pfConfig.times
      : pfConfig?.time ? [pfConfig.time] : ['09:00']
  )
  const [scheduleDays, setScheduleDays] = useState<DayOfWeek[]>(
    pfConfig?.days && pfConfig.days.length > 0
      ? pfConfig.days
      : ['mon', 'tue', 'wed', 'thu', 'fri']
  )
  const [scheduleDaysOfMonth, setScheduleDaysOfMonth] = useState<number[]>(
    pfConfig?.days_of_month && pfConfig.days_of_month.length > 0
      ? pfConfig.days_of_month
      : pfConfig?.day_of_month ? [pfConfig.day_of_month] : [1]
  )
  const [scheduleYearlyDates, setScheduleYearlyDates] =
    useState<Array<{ month: number; day: number }>>(() => {
      // Prefer new `dates: ['MM-DD']`; fall back to legacy
      // `{month, day_of_month}` from the source row when present.
      if (pfConfig?.dates && pfConfig.dates.length > 0) {
        return pfConfig.dates.map((d) => {
          const [m, dd] = d.split('-').map(Number)
          return { month: m ?? 1, day: dd ?? 1 }
        })
      }
      if (pfConfig?.month && pfConfig?.day_of_month) {
        return [{ month: pfConfig.month, day: pfConfig.day_of_month }]
      }
      return [{ month: 1, day: 1 }]
    })
  const [scheduleEndDate, setScheduleEndDate] = useState(pfConfig?.end_date ?? '')
  const [previewEnabled, setPreviewEnabled] = useState(
    Boolean(prefill?.preview_ahead_count && prefill?.preview_ahead_unit)
  )
  const [previewAheadCount, setPreviewAheadCount] = useState(prefill?.preview_ahead_count ?? 1)
  const [previewAheadUnit, setPreviewAheadUnit] = useState<'days' | 'weeks' | 'months' | 'years'>(
    prefill?.preview_ahead_unit ?? 'weeks'
  )
  const [vendorId, setVendorId] = useState(prefill?.vendor_id ?? '')
  const [category, setCategory] = useState(prefill?.category ?? '')
  const [reportTemplateId, setReportTemplateId] = useState(prefill?.report_template_id ?? '')
  // Multi-user assignment. Prefill precedence:
  //   1. `assignee_ids` (new — used by Duplicate)
  //   2. singular `assign_user` (legacy URL-param flow — Requests
  //      → Create WO deeplink still sends this)
  const [assigneeIds, setAssigneeIds] = useState<string[]>(
    prefill?.assignee_ids && prefill.assignee_ids.length > 0
      ? prefill.assignee_ids
      : prefill?.assign_user ? [prefill.assign_user] : []
  )
  const [teamId, setTeamId] = useState(prefill?.team_id ?? '')
  const [reporterId, setReporterId] = useState(prefill?.reporter_id ?? '')
  // Ordered list of approver user IDs. Sent one-at-a-time to addApprovalStep
  // after the WO row exists, since the API doesn't accept the full chain
  // in the create payload.
  const [approverIds, setApproverIds] = useState<string[]>([])
  // Per-WO digital-signature gates. Default off — admins opt-in only
  // on the tickets that actually need a paper trail. Server-side, the
  // close / acknowledge handlers reject the call without a fresh,
  // matching signature when these are true.
  const [requireCloseSig, setRequireCloseSig] = useState(prefill?.require_close_signature ?? false)
  const [requireAckSig, setRequireAckSig] = useState(prefill?.require_acknowledge_signature ?? false)
  // workflowId removed — workflows fire automatically based on triggers

  const sitesQ = useQuery({ queryKey: ['sites'], queryFn: () => api.sites.list(), staleTime: 5 * 60_000 })
  const sites: Site[] = Array.isArray(sitesQ.data) ? sitesQ.data : []

  const assetsQ = useQuery({ queryKey: ['assets'], queryFn: () => api.assets.list({}), staleTime: 60_000 })
  const assets: Asset[] = Array.isArray(assetsQ.data) ? assetsQ.data : []
  const filteredAssets = (() => {
    let list = assets
    if (siteId) list = list.filter((a) => a.site_id === siteId)
    if (locationId) list = list.filter((a) => a.location_id === locationId)
    return list
  })()

  // Locations are fetched inside LocationTreeSelect (which uses
  // `api.locations.tree(siteId)` — hierarchical view). The flat
  // `api.locations.list()` used to feed a SearchableSelect here; not
  // needed anymore since the tree component owns its own data.

  const vendorsQ = useQuery({ queryKey: ['vendors'], queryFn: () => api.vendors.list(), staleTime: 60_000 })
  const vendors: Vendor[] = Array.isArray(vendorsQ.data) ? vendorsQ.data : []

  const checklistsQ = useQuery({ queryKey: ['checklists', { status: 'published' }], queryFn: () => api.checklists.list({ status: 'published' }), staleTime: 60_000 })
  const checklists: ChecklistTemplateSummary[] = Array.isArray(checklistsQ.data) ? checklistsQ.data : []

  const reportTemplatesQ = useQuery({ queryKey: ['report-templates-summary'], queryFn: () => api.reportTemplates.listSummaries(), staleTime: 60_000 })
  const reportTemplates = Array.isArray(reportTemplatesQ.data) ? reportTemplatesQ.data : []

  // Scoped by the currently-picked site — the backend restricts to
  // people with a user_site_access grant for it (admin-class roles
  // always included). Empty siteId = unscoped (still customer-scoped
  // server-side via the active-customer header, same as every other
  // list in this app). `placeholderData` keeps the previous site's
  // list on screen while the new one loads, instead of the picker
  // flashing empty and the clear-invalid-assignee effect below
  // misfiring against a momentarily-empty array.
  const usersQ = useQuery({
    queryKey: ['users', siteId],
    queryFn: () => api.users.list({ site_id: siteId || undefined }),
    placeholderData: (prev) => prev,
    staleTime: 60_000,
  })
  const users = Array.isArray(usersQ.data) ? usersQ.data : []

  // When the admin picks an assignee, restrict the Site picker to
  // sites that person is actually allowed to work at (per the
  // user_site_access table). Admin-class roles (org_admin /
  // super_admin / customer_admin) don't carry explicit grants — they
  // implicitly see every site — so we don't filter for them.
  const ADMIN_ROLES = new Set(['org_admin', 'super_admin', 'customer_admin'])
  // Multi-user assignment: use the FIRST picked user as the
  // "primary" for site-scope hints. Downstream assignees will get
  // whatever site the admin ends up picking — the flow assumes
  // everyone on a WO can access it, otherwise the admin wouldn't
  // have picked them together.
  const primaryAssigneeId = assigneeIds[0] ?? ''
  const assigneeUser = users.find((u: any) => u.id === primaryAssigneeId)
  const assigneeIsAdmin = assigneeUser ? ADMIN_ROLES.has(assigneeUser.role) : false
  const assigneeSitesQ = useQuery({
    queryKey: ['user-sites', primaryAssigneeId],
    queryFn: () => api.users.listSites(primaryAssigneeId),
    enabled: Boolean(primaryAssigneeId) && !assigneeIsAdmin,
    staleTime: 60_000,
  })
  const assigneeSiteIds = useMemo(() => {
    if (!primaryAssigneeId || assigneeIsAdmin) return null // null → no filter
    if (!assigneeSitesQ.data) return null                  // still loading → no filter yet
    return new Set(assigneeSitesQ.data.map((s) => s.id))
  }, [primaryAssigneeId, assigneeIsAdmin, assigneeSitesQ.data])
  const scopedSites = useMemo(() => {
    if (!assigneeSiteIds) return sites
    return sites.filter((s) => assigneeSiteIds.has(s.id))
  }, [sites, assigneeSiteIds])
  // If the admin already picked a site that the new assignee can't
  // access, clear it (and the dependent location + asset selections)
  // so the form can't submit an unworkable combination.
  useEffect(() => {
    if (siteId && assigneeSiteIds && !assigneeSiteIds.has(siteId)) {
      setSiteId('')
      setLocationId('')
      setAssetId('')
    }
  }, [siteId, assigneeSiteIds])
  // Mirror of the effect above, other direction: once `users` is
  // scoped to the newly-picked site (not still showing the previous
  // site's placeholder data), drop any already-selected assignee or
  // reporter/reviewer who isn't in it — otherwise a stale pick from
  // before the site change would silently ride along into the submit
  // payload. Reporter/Reviewer shares the same `users` list (see the
  // SearchableSelect below), so it needs the same guard.
  useEffect(() => {
    if (!siteId || usersQ.isPlaceholderData) return
    const validIds = new Set(users.map((u: any) => u.id))
    setAssigneeIds((prev) => {
      const filtered = prev.filter((id) => validIds.has(id))
      return filtered.length === prev.length ? prev : filtered
    })
    setReporterId((prev) => (prev && !validIds.has(prev) ? '' : prev))
  }, [siteId, users, usersQ.isPlaceholderData])

  // Scoped by site the same way usersQ is above — a team only counts
  // as assignable here if at least one of its members has access to
  // the picked site (see teams.service.js's listTeams).
  const teamsQ = useQuery({
    queryKey: ['teams', siteId],
    queryFn: () => api.teams.list({ site_id: siteId || undefined }),
    placeholderData: (prev) => prev,
    staleTime: 60_000,
  })
  const teams: Team[] = Array.isArray(teamsQ.data) ? teamsQ.data : []
  // Same guard as the users effect above, for the team picker.
  useEffect(() => {
    if (!siteId || teamsQ.isPlaceholderData) return
    const validTeamIds = new Set(teams.map((t) => t.id))
    setTeamId((prev) => (prev && !validTeamIds.has(prev) ? '' : prev))
  }, [siteId, teams, teamsQ.isPlaceholderData])

  const categoriesQ = useQuery({ queryKey: ['asset-categories'], queryFn: () => api.assets.listCategories(), staleTime: 60_000 })
  const categories: AssetCategory[] = Array.isArray(categoriesQ.data) ? categoriesQ.data : []

  const workflowsQ = useQuery({ queryKey: ['workflows'], queryFn: () => api.workflows.list(), staleTime: 60_000 })
  const allWorkflows = Array.isArray(workflowsQ.data) ? workflowsQ.data : []

  const selectedAsset = assets.find((a) => a.id === assetId)
  useEffect(() => {
    if (selectedAsset) {
      if (selectedAsset.site_id && !siteId) setSiteId(selectedAsset.site_id)
      if (selectedAsset.location_id && !locationId) setLocationId(selectedAsset.location_id)
      if (selectedAsset.category_name && !category) setCategory(selectedAsset.category_name)
      const meta = (selectedAsset.metadata ?? {}) as Record<string, any>
      if (meta.vendor_id && !vendorId) setVendorId(meta.vendor_id)
      // Auto-tag checklists linked to this asset. The location's
      // checklists are picked up by the separate effect below — that
      // way locations chosen WITHOUT an asset still get their list
      // attached. The Set merge dedupes if both paths return the same
      // template.
      api.assets.listChecklists(selectedAsset.id).then((cls) => {
        const ids = (cls ?? []).map((c) => c.template_id)
        if (ids.length > 0) setChecklistIds((prev) => [...new Set([...prev, ...ids])])
      }).catch(() => {})
    }
  }, [assetId])

  // Auto-tag checklists linked directly to the chosen location. Fires
  // whenever locationId changes — whether the user picked the location
  // by hand or it got prefilled by an asset selection. Without this
  // effect, picking a location without an asset would never pull in
  // the location's checklists.
  useEffect(() => {
    if (!locationId) return
    api.locations.listChecklists(locationId).then((cls) => {
      const ids = (cls ?? []).map((c) => c.template_id)
      if (ids.length > 0) setChecklistIds((prev) => [...new Set([...prev, ...ids])])
    }).catch(() => {})
  }, [locationId])

  const estimatedMinutes = (parseInt(estHours) || 0) * 60 + (parseInt(estMins) || 0)

  // Build the scheduling payload. All wall-clock inputs are interpreted
  // as `orgTz` and converted to UTC for the API. The recurring config
  // stashes `tz` so the server-side scheduler knows which zone to
  // evaluate calendar dates in (matters for DST + non-UTC orgs).
  function buildSchedulePayload(): { scheduled_for?: string | null; schedule_config?: (ScheduleConfig & { tz?: string }) | null } {
    if (scheduleMode === 'off') return {}
    const orgTzIso = (wall: string) => {
      if (!wall) return null
      const d = tzWallToUtc(wall, orgTz)
      return d ? d.toISOString() : null
    }
    if (scheduleMode === 'one_shot') {
      return { scheduled_for: orgTzIso(scheduledFor) }
    }
    // recurring — first activation is the next matching calendar slot
    // (computed server-side). No explicit start moment is required from
    // the admin; if they want to delay, they can switch to one-shot for
    // the first run and add the recurring later.
    //
    // We send BOTH `time` and `times`: `times` is the new multi-time-of-day
    // field, and `time` is kept as the first entry so any old reader
    // (server validators rolled back, an older mobile build) still sees
    // a valid schedule.
    const sortedTimes = [...new Set(scheduleTimes.filter((t) => /^\d{2}:\d{2}$/.test(t)))]
      .sort()
    const safeTimes = sortedTimes.length > 0 ? sortedTimes : ['09:00']
    const cfg: ScheduleConfig & { tz: string } = {
      type: scheduleType,
      time: safeTimes[0],
      times: safeTimes,
      tz: orgTz,
    }
    if (scheduleType === 'weekly') cfg.days = scheduleDays
    if (scheduleType === 'monthly' || scheduleType === 'quarterly') {
      // Always send the new `days_of_month` array. Mirror the first
      // entry into legacy `day_of_month` so an older client / reader
      // (mobile build that hasn't been updated) still sees a valid
      // single-day schedule.
      const sortedDoms = [...new Set(scheduleDaysOfMonth)]
        .filter((n) => Number.isInteger(n) && n >= 1 && n <= 31)
        .sort((a, b) => a - b)
      const doms = sortedDoms.length > 0 ? sortedDoms : [1]
      cfg.days_of_month = doms
      cfg.day_of_month = doms[0]
    }
    if (scheduleType === 'yearly') {
      // Same dual-write: new `dates: ['MM-DD']` array plus legacy
      // `{month, day_of_month}` from the first entry.
      const sorted = [...scheduleYearlyDates]
        .filter((d) => d.month >= 1 && d.month <= 12 && d.day >= 1 && d.day <= 31)
        .sort((a, b) => a.month - b.month || a.day - b.day)
      const safe = sorted.length > 0 ? sorted : [{ month: 1, day: 1 }]
      const pad = (n: number) => String(n).padStart(2, '0')
      cfg.dates = safe.map((d) => `${pad(d.month)}-${pad(d.day)}`)
      const first = safe[0]!
      cfg.month = first.month
      cfg.day_of_month = first.day
    }
    if (scheduleEndDate) cfg.end_date = scheduleEndDate
    return { schedule_config: cfg }
  }

  const createM = useMutation({
    mutationFn: async () => {
      const schedule = buildSchedulePayload()

      if (isEdit && workOrderId) {
        // No `site_id` / `kind` here — both are immutable server-side
        // (the update endpoint's schema doesn't even accept `site_id`,
        // and would 400 the whole request under `additionalProperties:
        // false` if we sent it).
        // This branch only runs on edit, so every cleared field below
        // sends an explicit `null` (not `undefined`) — omitting a key
        // means "leave unchanged" to the backend's sparse update, so a
        // field the user just cleared would otherwise silently survive
        // a "successful" save. Booleans are sent as-is (not `|| undefined`)
        // so turning a signature requirement OFF actually persists.
        const wo = await apiNs.update(workOrderId, {
          title: title.trim(), description: description.trim() || null,
          priority,
          asset_id: isTask ? null : (assetId || null),
          location_id: locationId || selectedAsset?.location_id || null,
          due_date: dueDate || null,
          start_date: startDate || null,
          estimated_minutes: estimatedMinutes > 0 ? estimatedMinutes : null,
          work_type: workType,
          checklist_id: checklistIds[0] || null,
          vendor_id: isTask ? null : (vendorId || null),
          category: category || null,
          report_template_id: reportTemplateId || null,
          reporter_id: reporterId || null,
          require_close_signature: requireCloseSig,
          require_acknowledge_signature: requireAckSig,
          preview_ahead_count: (scheduleMode === 'recurring' && previewEnabled)
            ? previewAheadCount : undefined,
          preview_ahead_unit:  (scheduleMode === 'recurring' && previewEnabled)
            ? previewAheadUnit : undefined,
          ...schedule,
        })
        // Assign is a full replace (delete-then-insert on the server),
        // so — unlike create, which only bothers calling this when
        // something was actually picked — edit must call it
        // unconditionally: an admin clearing every assignee is a real,
        // valid edit and has to reach the server as an empty set, not
        // get skipped because the array happened to be empty.
        await apiNs.assign(workOrderId, {
          assignee_ids: assigneeIds,
          assigned_to_team_id: teamId || undefined,
        })
        // Additional linked checklists beyond the primary — additive
        // only, same as create. Removing a previously-linked checklist
        // isn't handled by this form; that's managed from the WO
        // detail page's own checklist section.
        for (const tid of checklistIds.slice(1)) {
          await apiNs.addLinkedChecklist(wo.id, tid).catch(() => {})
        }
        return wo
      }

      // Per-checklist inputs are time-of-day (HH:MM) only. Combine with
      const wo = await apiNs.create({
        kind,
        title: title.trim(), description: description.trim() || undefined,
        priority, site_id: siteId,
        // Tasks are soft-services work — strip asset / vendor at the
        // client so the user can't accidentally send a task with an
        // asset tied to it (the backend would reject it anyway).
        asset_id: isTask ? undefined : (assetId || undefined),
        location_id: locationId || selectedAsset?.location_id || undefined,
        due_date: dueDate || undefined,
        start_date: startDate || undefined,
        estimated_minutes: estimatedMinutes > 0 ? estimatedMinutes : undefined,
        work_type: workType,
        checklist_id: checklistIds[0] || undefined,
        vendor_id: isTask ? undefined : (vendorId || undefined),
        category: category || undefined,
        report_template_id: reportTemplateId || undefined,
        reporter_id: reporterId || prefill?.reporter_id || undefined,
        require_close_signature: requireCloseSig || undefined,
        require_acknowledge_signature: requireAckSig || undefined,
        // Preview window is only meaningful on recurring templates; the
        // server strips it on one-shots and instant WOs.
        preview_ahead_count: (scheduleMode === 'recurring' && previewEnabled)
          ? previewAheadCount : undefined,
        preview_ahead_unit:  (scheduleMode === 'recurring' && previewEnabled)
          ? previewAheadUnit : undefined,
        ...schedule,
      })
      if (assigneeIds.length > 0 || teamId) {
        await apiNs.assign(wo.id, {
          assignee_ids: assigneeIds.length > 0 ? assigneeIds : undefined,
          assigned_to_team_id: teamId || undefined,
        })
      }
      // Link any additional checklists to the WO. The primary one is
      // already attached via `checklist_id` on create; the rest go in
      // the junction table. No per-checklist execution window.
      for (const tid of checklistIds.slice(1)) {
        await apiNs.addLinkedChecklist(wo.id, tid).catch(() => {})
      }
      // Approval chain — must be inserted after the WO row exists. Send
      // sequentially so step_order matches the displayed order.
      for (const approverId of approverIds) {
        await apiNs.addApprovalStep(wo.id, { approver_id: approverId }).catch(() => {})
      }
      // Link workflow
      // Workflows fire automatically via the trigger engine — no manual linking needed
      if (prefill?.request_id) {
        // Auto-approve any still-submitted request (defensive — the UI
        // now gates Create WO to approved-only, but a stale URL could
        // land here with a submitted id). Approve is safe to fail
        // silently — it 404s if the request is already reviewed.
        //
        // CRITICAL: pass `create_work_order: false`. The approve
        // endpoint defaults to `true`, and if we somehow hit this path
        // with a still-submitted request, letting it auto-create a
        // ghost WO would (a) hoard the request.work_order_id slot and
        // (b) make the linkWorkOrder call below 409, orphaning the
        // WO the user actually just built.
        await api.requests.approve(prefill.request_id, { create_work_order: false })
          .catch((err) => {
            // 404 means "already reviewed" which is fine and expected.
            // Anything else — log so we can see it during debugging.
            if (err instanceof ApiError && err.status !== 404) {
              // eslint-disable-next-line no-console
              console.warn('[create-wo] approve fallback threw:', err.status, err.message)
            }
          })
        // Link the new WO to the request. Two failure modes we handle
        // explicitly:
        //   • 409 = a WO was ALREADY linked (usually by a workflow's
        //     link_to_request that ran on request.submitted). The
        //     user's manually-created WO is orphaned relative to the
        //     request. Tell them, and offer a clear next step.
        //   • Anything else = generic failure, show the raw message.
        try {
          await api.requests.linkWorkOrder(prefill.request_id, wo.id)
        } catch (err) {
          if (err instanceof ApiError && err.status === 409) {
            // eslint-disable-next-line no-console
            console.warn('[create-wo] linkWorkOrder 409 — request already linked to another WO:',
              (err as any)?.data?.work_order_id)
            toast.error(
              `This request is already linked to another ${isTask ? 'task' : 'work order'} (probably created by a workflow). ` +
              `Your new ${isTask ? 'task' : 'work order'} was created but is not linked to this request.`
            )
          } else {
            toast.error(
              err instanceof ApiError
                ? `${isTask ? 'Task' : 'Work order'} created, but failed to link to the request: ${err.message}`
                : `${isTask ? 'Task' : 'Work order'} created, but failed to link to the request.`
            )
          }
        }
      }
      return wo
    },
    onSuccess: async () => {
      const noun = isTask ? 'Task' : 'Work order'
      toast.success(isEdit ? `${noun} updated` : `${noun} created`)
      // `refetchQueries` (not invalidate) so the requests cache is
      // rebuilt inline before we close the dialog. Without this the
      // user could land on /requests before the invalidated fetch
      // finished, still see the pre-link buttons, and reasonably
      // conclude "linking is broken." Awaiting also keeps the toast
      // visible during the fetch. Only really needed when there's a
      // `request_id` prefill, but cheap enough to always run.
      await qc.refetchQueries({ queryKey: ['requests'] })
      onCreated()
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const iconSx = { fontSize: 16, color: 'text.secondary', mr: 1 }

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>
        {isEdit
          ? (isTask ? 'Edit task' : 'Edit work order')
          : (isTask ? 'New task' : 'New work order')}
        <Typography component="span" sx={{
          fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
          ml: 1.5, px: 1, py: 0.25, borderRadius: 1,
          bgcolor: isTask ? '#EFF6FF' : '#F0FDFA',
          color:   isTask ? '#1D4ED8' : '#0F766E',
        }}>
          {isTask ? 'Soft services' : 'Maintenance'}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mt: 1 }}>
          {/* Left column */}
          <Stack spacing={2.5} sx={{ flex: 1, minWidth: 0 }}>
            <SectionLabel>General</SectionLabel>
            <TextField label="Title" required value={title} onChange={(e) => setTitle(e.target.value)} size="small" fullWidth autoFocus
              placeholder="e.g. Replace HVAC filter on Unit A-101" />
            <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} size="small" fullWidth multiline minRows={3}
              placeholder="Describe what needs to be done..." />
            <Stack direction="row" spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Select value={priority} onChange={(e) => setPriority(e.target.value as WorkOrderPriority)} size="small" fullWidth
                  renderValue={(v) => (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: PRIORITY_COLORS[v] }} />
                      <span style={{ textTransform: 'capitalize' }}>{v} priority</span>
                    </Stack>
                  )}>
                  {(['low', 'medium', 'high', 'critical'] as WorkOrderPriority[]).map((p) => (
                    <MenuItem key={p} value={p}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: PRIORITY_COLORS[p] }} />
                        <Typography sx={{ fontSize: 13, textTransform: 'capitalize' }}>{p} priority</Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Select value={workType} onChange={(e) => setWorkType(e.target.value as WorkOrderType)} size="small" fullWidth>
                  <MenuItem value="reactive">Reactive</MenuItem>
                  <MenuItem value="preventive">Preventive</MenuItem>
                  <MenuItem value="inspection">Inspection</MenuItem>
                  <MenuItem value="safety">Safety</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </Box>
            </Stack>

            <Divider />
            <SectionLabel>Assignment</SectionLabel>
            {/* Multi-user picker — every selected user gets the WO
                in their assigned list and is emailed on updates
                (assigned / commented / overdue / SLA / closed). */}
            <SearchableSelect
              multiple
              value={assigneeIds}
              onChange={setAssigneeIds}
              options={users}
              getValue={(u: any) => u.id}
              getLabel={(u: any) => u.name}
              placeholder="Assign to one or more people"
              renderOption={(u: any) => (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <UserAvatar name={u.name} avatarUrl={u.avatar_url} size={22} sx={{ bgcolor: '#0D9488' }} />
                  <Box>
                    <Typography sx={{ fontSize: 13 }}>{u.name}</Typography>
                    <Typography sx={{ fontSize: 10.5, color: 'text.secondary' }}>{u.role?.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}</Typography>
                  </Box>
                </Stack>
              )}
            />

            <SearchableSelect
              value={teamId}
              onChange={setTeamId}
              options={teams}
              getValue={(t) => t.id}
              getLabel={(t) => t.name}
              placeholder="Assign to team"
              renderOption={(t) => (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Avatar variant="rounded" sx={{ width: 22, height: 22, fontSize: 10, fontWeight: 700, bgcolor: '#7C3AED' }}><GroupsOutlinedIcon sx={{ fontSize: 14 }} /></Avatar>
                  <Box>
                    <Typography sx={{ fontSize: 13 }}>{t.name}</Typography>
                    <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{t.member_count} members</Typography>
                  </Box>
                </Stack>
              )}
            />

            <Divider />
            <SectionLabel>Location & asset</SectionLabel>
            <SearchableSelect
              value={siteId}
              onChange={(v) => { setSiteId(v); setLocationId(''); setAssetId('') }}
              options={scopedSites}
              getValue={(s) => s.id}
              getLabel={formatSiteName}
              disabled={isEdit}
              placeholder={scopedSites.length === 0 && primaryAssigneeId && !assigneeIsAdmin
                ? `${assigneeUser?.name ?? 'Assignee'} has no site access`
                : 'Select site *'}
              renderOption={(s) => (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <LocationOnOutlinedIcon sx={{ fontSize: 16, color: '#3B82F6' }} />
                  <Typography sx={{ fontSize: 13 }}>{s.name}</Typography>
                </Stack>
              )}
            />
            {isEdit && (
              <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: -0.75 }}>
                Site can't be changed after creation — delete and recreate to move it to a different site.
              </Typography>
            )}
            {!isEdit && primaryAssigneeId && !assigneeIsAdmin && assigneeSiteIds && (
              <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: -0.75 }}>
                {scopedSites.length === 0
                  ? `No sites granted to ${assigneeUser?.name ?? 'this user'}. Grant access in Settings → People before assigning.`
                  : `Showing ${scopedSites.length} of ${sites.length} site${sites.length === 1 ? '' : 's'} — limited to ${assigneeUser?.name ?? 'this user'}'s granted access.`}
              </Typography>
            )}

            {/* Hierarchical location picker — surfaces the full site
                → building → floor → room tree so users can drill in
                instead of hunting through a flat autocomplete list.
                Falls back to the disabled-input state when no site is
                picked yet (same UX as the previous SearchableSelect). */}
            <LocationTreeSelect
              siteId={siteId || null}
              value={locationId}
              onChange={(v) => { setLocationId(v); setAssetId('') }}
              placeholder={siteId ? 'Select location (optional)' : 'Pick a site first'}
            />

            {!isTask && (
              <SearchableSelect
                value={assetId}
                onChange={setAssetId}
                options={filteredAssets}
                getValue={(a) => a.id}
                getLabel={(a) => `${a.name}${a.asset_code ? ` (${a.asset_code})` : ''}`}
                placeholder="Select asset (optional)"
                renderOption={(a) => (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Avatar variant="rounded" sx={{ width: 22, height: 22, fontSize: 10, fontWeight: 700, bgcolor: '#EEF2FF', color: '#6366F1' }}>{a.asset_code?.slice(-2)}</Avatar>
                    <Box>
                      <Typography sx={{ fontSize: 13 }}>{a.name}</Typography>
                      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{a.asset_code}</Typography>
                    </Box>
                  </Stack>
                )}
              />
            )}

            <Divider />
            <SectionLabel>Attach files</SectionLabel>
            <Box sx={{ py: 2.5, textAlign: 'center', borderRadius: 2, border: '1px dashed', borderColor: 'divider' }}>
              <UploadFileOutlinedIcon sx={{ fontSize: 22, color: 'text.disabled', mb: 0.5 }} />
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>You can attach files after creating the {isTask ? 'task' : 'work order'}</Typography>
            </Box>
          </Stack>

          {/* Right column */}
          <Stack spacing={2.5} sx={{ flex: 1, minWidth: 0 }}>
            <SectionLabel>Estimated effort</SectionLabel>
            <Stack direction="row" spacing={2}>
              <TextField label="Est. hours" type="number" value={estHours} onChange={(e) => setEstHours(e.target.value)}
                size="small" fullWidth placeholder="0" />
              <TextField label="Est. minutes" type="number" value={estMins} onChange={(e) => setEstMins(e.target.value)}
                size="small" fullWidth placeholder="0" />
            </Stack>

            <Divider />
            <SectionLabel>{isTask ? 'Task type' : 'Schedule'}</SectionLabel>
            <Select
              value={scheduleMode}
              onChange={(e) => setScheduleMode(e.target.value as 'off' | 'one_shot' | 'recurring')}
              size="small" fullWidth>
              <MenuItem value="off">{isTask ? 'Unscheduled — start immediately' : 'Create now'}</MenuItem>
              <MenuItem value="one_shot">{isTask ? 'Scheduled — specific date / time' : 'Schedule for a specific date/time'}</MenuItem>
              <MenuItem value="recurring">{isTask ? 'Scheduled — recurring' : 'Recurring (calendar-based)'}</MenuItem>
            </Select>

            {scheduleMode === 'off' && (
              <Stack direction="row" spacing={2}>
                <TextField label="Start date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                  size="small" fullWidth InputLabelProps={{ shrink: true }} />
                <TextField label="Due date" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
                  size="small" fullWidth InputLabelProps={{ shrink: true }} />
              </Stack>
            )}

            {scheduleMode === 'one_shot' && (
              <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 1 }}>
                  Activate this work order at
                </Typography>
                <TextField
                  type="datetime-local"
                  value={scheduledFor}
                  onChange={(e) => setScheduledFor(e.target.value)}
                  size="small" fullWidth
                />
                <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 1, mb: 2 }}>
                  Interpreted {tzLabel}. Until this time, the WO stays in
                  the "Scheduled" section and is not visible to assigned techs.
                </Typography>
                <TextField label="Due date" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
                  size="small" fullWidth InputLabelProps={{ shrink: true }} />
              </Box>
            )}

            {scheduleMode === 'recurring' && (
              <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Typography sx={{ fontSize: 12.5, fontWeight: 500, whiteSpace: 'nowrap' }}>
                      Every
                    </Typography>
                    <Select
                      value={scheduleType}
                      onChange={(e) => setScheduleType(e.target.value as ScheduleConfigType)}
                      size="small" sx={{ flex: 1 }}>
                      <MenuItem value="daily">day</MenuItem>
                      <MenuItem value="weekly">week (specific days)</MenuItem>
                      <MenuItem value="monthly">month</MenuItem>
                      <MenuItem value="quarterly">quarter</MenuItem>
                      <MenuItem value="yearly">year</MenuItem>
                    </Select>
                  </Stack>

                  {scheduleType === 'weekly' && (
                    <Box>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 1 }}>
                        Days
                      </Typography>
                      <ToggleButtonGroup
                        value={scheduleDays}
                        onChange={(_, val: DayOfWeek[]) => setScheduleDays(val)}
                        size="small" sx={{ gap: 0.5, flexWrap: 'wrap' }}>
                        {DAY_LABELS.map((d) => (
                          <ToggleButton key={d.key} value={d.key}
                            sx={{ width: 36, height: 36, borderRadius: '50% !important', border: '1px solid', borderColor: 'divider !important',
                              fontSize: 12, fontWeight: 700,
                              '&.Mui-selected': { bgcolor: '#0D9488', color: '#fff', '&:hover': { bgcolor: '#0F766E' } } }}>
                            {d.short}
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                    </Box>
                  )}

                  {(scheduleType === 'monthly' || scheduleType === 'quarterly') && (
                    <Stack spacing={1}>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 500, whiteSpace: 'nowrap' }}>
                          On day{scheduleDaysOfMonth.length === 1 ? '' : 's'}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                          (tap to toggle — pick one or many; spawns once per selected day{scheduleType === 'quarterly' ? ' in the first month of each quarter' : ''})
                        </Typography>
                      </Stack>
                      {/* Day chips 1-31 — tap to toggle. Mirrors the
                          weekly day-picker pattern so multi-day looks
                          and feels the same across cadences. */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {Array.from({ length: 31 }, (_, i) => i + 1).map((n) => {
                          const on = scheduleDaysOfMonth.includes(n)
                          return (
                            <Box key={n}
                              onClick={() => {
                                setScheduleDaysOfMonth((prev) => {
                                  if (prev.includes(n)) {
                                    const next = prev.filter((x) => x !== n)
                                    // Keep at least one — last tap is a no-op.
                                    return next.length === 0 ? prev : next
                                  }
                                  return [...prev, n].sort((a, b) => a - b)
                                })
                              }}
                              sx={{
                                minWidth: 28, height: 28,
                                px: 0.75, borderRadius: 1,
                                border: '1px solid',
                                borderColor: on ? 'primary.main' : 'divider',
                                bgcolor: on ? 'primary.main' : 'background.paper',
                                color: on ? 'primary.contrastText' : 'text.secondary',
                                fontSize: 12, fontWeight: 600,
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', userSelect: 'none',
                                '&:hover': { borderColor: 'primary.main' },
                              }}>
                              {n}
                            </Box>
                          )
                        })}
                      </Box>
                      <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>
                        Days past the end of a month clamp to the last day (so "31" still fires in Feb).
                      </Typography>
                    </Stack>
                  )}

                  {scheduleType === 'yearly' && (
                    <Stack spacing={1}>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 500, whiteSpace: 'nowrap' }}>
                          {scheduleYearlyDates.length === 1 ? 'On' : `${scheduleYearlyDates.length} dates per year:`}
                        </Typography>
                        <Box sx={{ flex: 1 }} />
                        <Button size="small"
                          onClick={() => setScheduleYearlyDates((d) => [...d, { month: 1, day: 1 }])}
                          sx={{ fontSize: 11.5, color: 'primary.main', minWidth: 0 }}>
                          + Add date
                        </Button>
                      </Stack>
                      <Stack spacing={0.75}>
                        {scheduleYearlyDates.map((entry, idx) => (
                          <Stack key={idx} direction="row" alignItems="center" spacing={1}>
                            <Select size="small" value={entry.month}
                              onChange={(e) => {
                                const m = Number(e.target.value)
                                setScheduleYearlyDates((arr) => arr.map((d, i) => i === idx ? { ...d, month: m } : d))
                              }} sx={{ flex: 1 }}>
                              {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => (
                                <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>
                              ))}
                            </Select>
                            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>day</Typography>
                            <Select size="small" value={entry.day}
                              onChange={(e) => {
                                const day = Number(e.target.value)
                                setScheduleYearlyDates((arr) => arr.map((d, i) => i === idx ? { ...d, day } : d))
                              }} sx={{ width: 80 }}>
                              {Array.from({ length: 31 }, (_, i) => i + 1).map((n) => (
                                <MenuItem key={n} value={n}>{n}</MenuItem>
                              ))}
                            </Select>
                            {scheduleYearlyDates.length > 1 && (
                              <IconButton size="small"
                                onClick={() => setScheduleYearlyDates((arr) => arr.filter((_, i) => i !== idx))}
                                sx={{ color: 'text.secondary' }}>
                                <CloseIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            )}
                          </Stack>
                        ))}
                      </Stack>
                    </Stack>
                  )}

                  <Box>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>
                        Time(s) of day
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => setScheduleTimes((arr) => [...arr, '09:00'])}
                        startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                        sx={{ textTransform: 'none', fontSize: 12, fontWeight: 600 }}
                      >
                        Add time
                      </Button>
                    </Stack>
                    <Stack spacing={1}>
                      {scheduleTimes.map((t, i) => (
                        <Stack key={i} direction="row" alignItems="center" spacing={1.5}>
                          <TextField
                            type="time"
                            value={t}
                            onChange={(e) => setScheduleTimes((arr) =>
                              arr.map((v, j) => j === i ? e.target.value : v))}
                            size="small" sx={{ width: 130 }}
                            inputProps={{ step: 300 }}
                          />
                          <Typography sx={{ fontSize: 11, color: 'text.disabled', flex: 1 }}>
                            {tzLabel}
                          </Typography>
                          {scheduleTimes.length > 1 && (
                            <IconButton
                              size="small"
                              onClick={() => setScheduleTimes((arr) =>
                                arr.filter((_, j) => j !== i))}
                              sx={{ color: 'text.secondary' }}
                            >
                              <CloseIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          )}
                        </Stack>
                      ))}
                    </Stack>
                    {scheduleTimes.length > 1 && (
                      <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.75 }}>
                        {scheduleTimes.length} work orders will spawn each matching day.
                      </Typography>
                    )}
                  </Box>

                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 500, whiteSpace: 'nowrap' }}>
                      Stops on
                    </Typography>
                    <TextField
                      type="date"
                      value={scheduleEndDate}
                      onChange={(e) => setScheduleEndDate(e.target.value)}
                      size="small" sx={{ width: 170 }}
                      InputLabelProps={{ shrink: true }}
                    />
                    {scheduleEndDate ? (
                      <Button size="small" onClick={() => setScheduleEndDate('')}
                        sx={{ textTransform: 'none', fontSize: 12, color: 'text.secondary' }}>
                        Clear
                      </Button>
                    ) : (
                      <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                        optional — leave blank for no end
                      </Typography>
                    )}
                  </Stack>

                  {/* Look-ahead window — toggling on lets supervisors /
                      techs see upcoming spawns in the WO list as
                      read-only "Upcoming" rows so they can plan. */}
                  <Box sx={{ pt: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox size="small" checked={previewEnabled}
                          onChange={(e) => setPreviewEnabled(e.target.checked)} />
                      }
                      label={
                        <Typography sx={{ fontSize: 12.5, fontWeight: 500 }}>
                          Show upcoming work orders ahead of time
                        </Typography>
                      }
                    />
                    {previewEnabled && (
                      <Stack direction="row" alignItems="center" spacing={1.5}
                        sx={{ mt: 0.5, ml: 3.5 }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 500, whiteSpace: 'nowrap' }}>
                          Show
                        </Typography>
                        <TextField type="number" size="small"
                          value={previewAheadCount}
                          onChange={(e) => {
                            const n = parseInt(e.target.value, 10)
                            setPreviewAheadCount(Number.isFinite(n) && n > 0 ? n : 1)
                          }}
                          inputProps={{ min: 1, max: 365 }}
                          sx={{ width: 80 }} />
                        <Select size="small" value={previewAheadUnit}
                          onChange={(e) => setPreviewAheadUnit(e.target.value as typeof previewAheadUnit)}
                          sx={{ width: 110 }}>
                          <MenuItem value="days">days</MenuItem>
                          <MenuItem value="weeks">weeks</MenuItem>
                          <MenuItem value="months">months</MenuItem>
                          <MenuItem value="years">years</MenuItem>
                        </Select>
                        <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>
                          ahead — read-only rows in the list
                        </Typography>
                      </Stack>
                    )}
                  </Box>
                </Stack>
              </Box>
            )}

            <Divider />
            <SectionLabel>Approval chain</SectionLabel>
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: -0.5 }}>
              Who must sign off when the work is done? Approvers are notified
              in order; each one acts before the next is asked.
            </Typography>
            {approverIds.length > 0 && (
              <Stack spacing={0.75}>
                {approverIds.map((uid, idx) => {
                  const u = users.find((x) => x.id === uid)
                  return (
                    <Stack key={uid} direction="row" alignItems="center" spacing={1.25}
                      sx={{ p: 1, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.disabled', width: 18 }}>
                        {idx + 1}.
                      </Typography>
                      <UserAvatar name={u?.name} avatarUrl={u?.avatar_url} size={22} sx={{ bgcolor: '#0D9488' }} />
                      <Typography sx={{ fontSize: 13, flex: 1 }}>{u?.name ?? 'Unknown'}</Typography>
                      <IconButton size="small" disabled={idx === 0}
                        onClick={() => setApproverIds((prev) => {
                          const next = [...prev]
                          const tmp = next[idx - 1] as string
                          next[idx - 1] = next[idx] as string
                          next[idx] = tmp
                          return next
                        })}>
                        <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton size="small" disabled={idx === approverIds.length - 1}
                        onClick={() => setApproverIds((prev) => {
                          const next = [...prev]
                          const tmp = next[idx + 1] as string
                          next[idx + 1] = next[idx] as string
                          next[idx] = tmp
                          return next
                        })}>
                        <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => setApproverIds((prev) => prev.filter((_, i) => i !== idx))}>
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Stack>
                  )
                })}
              </Stack>
            )}
            <SearchableSelect
              value=""
              onChange={(uid) => {
                if (uid && !approverIds.includes(uid)) setApproverIds((prev) => [...prev, uid])
              }}
              options={users.filter((u) => !approverIds.includes(u.id))}
              getValue={(u) => u.id}
              getLabel={(u) => u.name}
              placeholder="Add approver…"
              renderOption={(u) => (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <UserAvatar name={u.name} avatarUrl={u.avatar_url} size={22} sx={{ bgcolor: '#0D9488' }} />
                  <Typography sx={{ fontSize: 13 }}>{u.name}</Typography>
                </Stack>
              )}
            />

            <Divider />
            <SectionLabel>Linked items</SectionLabel>
            <SearchableSelect
              multiple
              value={checklistIds}
              onChange={setChecklistIds}
              options={checklists}
              getValue={(c) => c.id}
              getLabel={(c) => c.name}
              placeholder="Select checklists"
              renderOption={(c) => (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                  <ChecklistOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography sx={{ fontSize: 13, flex: 1 }}>{c.name}</Typography>
                </Stack>
              )}
            />

            {!isTask && (
              <SearchableSelect
                value={vendorId}
                onChange={setVendorId}
                options={vendors}
                getValue={(v) => v.id}
                getLabel={(v) => v.name}
                placeholder="No vendor"
                renderOption={(v) => (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Avatar variant="rounded" sx={{ width: 22, height: 22, fontSize: 10, fontWeight: 700, bgcolor: '#FFF7ED', color: '#EA580C' }}>
                      {v.name?.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontSize: 13 }}>{v.name}</Typography>
                      {v.contact_name && <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{v.contact_name}</Typography>}
                    </Box>
                  </Stack>
                )}
              />
            )}

            <SearchableSelect
              value={category}
              onChange={setCategory}
              options={categories}
              getValue={(c) => c.name}
              getLabel={(c) => c.name}
              placeholder="Select category"
              renderOption={(c) => (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ width: 10, height: 10, borderRadius: 99, bgcolor: c.color ?? '#94A3B8' }} />
                  <Typography sx={{ fontSize: 13 }}>{c.name}</Typography>
                  {c.asset_count > 0 && <Chip label={c.asset_count} size="small" sx={{ height: 18, fontSize: 10, fontWeight: 600 }} />}
                </Stack>
              )}
            />

            <SearchableSelect
              value={reportTemplateId}
              onChange={setReportTemplateId}
              options={reportTemplates}
              getValue={(t) => t.id}
              getLabel={(t) => t.name}
              placeholder="Report template — use org default"
              renderOption={(t) => (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <DescriptionOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography sx={{ fontSize: 13, flex: 1 }}>{t.name}</Typography>
                  {t.is_default && <Chip label="Default" size="small" sx={{ height: 18, fontSize: 10, fontWeight: 600 }} />}
                </Stack>
              )}
            />

            <Divider />
            <SectionLabel>Reporter / Reviewer</SectionLabel>
            <SearchableSelect
              value={reporterId}
              onChange={setReporterId}
              options={users}
              getValue={(u: any) => u.id}
              getLabel={(u: any) => u.name}
              placeholder="Same as creator (default)"
              renderOption={(u: any) => (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <UserAvatar name={u.name} avatarUrl={u.avatar_url} size={22} sx={{ bgcolor: '#7C3AED' }} />
                  <Box>
                    <Typography sx={{ fontSize: 13 }}>{u.name}</Typography>
                    <Typography sx={{ fontSize: 10.5, color: 'text.secondary' }}>{u.role?.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}</Typography>
                  </Box>
                </Stack>
              )}
            />

            <Divider />
            <SectionLabel>Digital sign-off</SectionLabel>
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: -1, mb: 0.5 }}>
              Require a signature on this work order for the matching transition.
              The signer can draw with a pen or upload an image.
            </Typography>
            <FormControlLabel
              control={<Switch size="small" checked={requireCloseSig}
                onChange={(e) => setRequireCloseSig(e.target.checked)} />}
              label={<Typography sx={{ fontSize: 13 }}>Require signature to close</Typography>}
            />
            <FormControlLabel
              control={<Switch size="small" checked={requireAckSig}
                onChange={(e) => setRequireAckSig(e.target.checked)} />}
              label={<Typography sx={{ fontSize: 13 }}>Require signature to acknowledge</Typography>}
            />

            {/* Auto-matching workflows preview */}
            {allWorkflows.filter(w => {
              if (!w.is_active || w.trigger_type !== 'work_order.created') return false
              const cfg = w.trigger_config ?? {}
              if (cfg.priority && cfg.priority !== priority) return false
              if (cfg.work_type && cfg.work_type !== workType) return false
              return true
            }).length > 0 && (<>
              <Divider />
              <SectionLabel>Automations that will fire</SectionLabel>
              <Stack spacing={1}>
                {allWorkflows.filter(w => {
                  if (!w.is_active || w.trigger_type !== 'work_order.created') return false
                  const cfg = w.trigger_config ?? {}
                  if (cfg.priority && cfg.priority !== priority) return false
                  if (cfg.work_type && cfg.work_type !== workType) return false
                  return true
                }).map((w) => (
                  <Stack key={w.id} direction="row" alignItems="center" spacing={1.5}
                    sx={{ px: 2, py: 1.25, borderRadius: 1.5, border: '1px solid', borderColor: 'divider',
                      bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)' }}>
                    <BoltOutlinedIcon sx={{ fontSize: 16, color: '#7C3AED' }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 600 }} noWrap>{w.name}</Typography>
                      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{w.step_count} steps</Typography>
                    </Box>
                    <Chip label="Will auto-run" size="small" sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: '#7C3AED14', color: '#7C3AED' }} />
                  </Stack>
                ))}
              </Stack>
            </>)}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained" disabled={!title.trim() || !siteId || createM.isPending} onClick={() => createM.mutate()}>
          {isEdit
            ? (createM.isPending ? 'Saving...' : 'Save changes')
            : (createM.isPending ? 'Creating...' : (isTask ? 'Create task' : 'Create work order'))}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>{children}</Typography>
}

// ─── Minimal select ─────────────────────────────────────────────────────────

function MinimalSelect({ placeholder, value, onChange, options, width }: {
  placeholder: string; value: string; onChange: (v: string) => void; options: { value: string; label: string }[]; width: number
}) {
  const isDefault = value === 'all' || value === 'any'
  return (
    <Select size="small" value={value} onChange={(e) => onChange(e.target.value)}
      renderValue={(v) => {
        const opt = options.find((o) => o.value === v)
        return isDefault
          ? <Box component="span" sx={{ fontSize: 13, color: 'text.secondary', fontWeight: 500 }}>{placeholder}</Box>
          : <Box component="span" sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary' }}>{opt?.label}</Box>
      }}
      sx={{ height: 36, width, bgcolor: 'background.paper',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: isDefault ? 'divider' : 'primary.main' },
        '& .MuiSelect-select': { py: 0.75, px: 1.5 } }}>
      {options.map((o) => <MenuItem key={o.value} value={o.value} sx={{ fontSize: 13 }}>{o.label}</MenuItem>)}
    </Select>
  )
}
